import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { StickyNote } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ConfirmButton, SectionLabel } from '@/components/bookings/dialogKit'
import { createCalendarNote } from '@/lib/api'
import { readApiError } from '@/lib/apiError'
import { fmtDayDate } from '@/lib/format'
import { localDateTime, minutesToLabel, type SlotPick } from '@/lib/slotBooking'

// ─── "Add note" ────────────────────────────────────────────────────────────────
//
// A memo pinned to a point on the calendar for whoever is working that day.
//
// THE SLOT STAYS BOOKABLE. That is the entire difference from Block time, it is
// the reason calendar_notes is its own table rather than a flag on time_blocks,
// and the dialog says it out loud — an operator who believes a note holds the
// time will find it sold from under them.
//
// This is NOT a customer note. Nothing here reaches anyone's record: a note about
// a person belongs on that person, where it is searchable, taggable and part of
// their history. This is a note about the DAY.

// Matches MAX_NOTE_CHARS in the edge function. A memo, not a document.
const MAX_NOTE_CHARS = 500

export interface CalendarNoteDialogProps {
  pick: SlotPick | null
  onOpenChange: (open: boolean) => void
  /** Refetch the calendar once the memo lands. */
  onCreated?: () => void
}

export function CalendarNoteDialog({ pick, onOpenChange, onCreated }: CalendarNoteDialogProps) {
  const open = pick !== null

  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setNote('')
    setSaving(false)
    setError(null)
  }, [open, pick?.date, pick?.startMinutes, pick?.resourceId])

  const trimmed = note.trim()
  const canSave = pick !== null && trimmed.length > 0 && trimmed.length <= MAX_NOTE_CHARS && !saving
  const scopeLabel = pick?.resourceName ?? null

  async function save() {
    if (!canSave || !pick) return
    setSaving(true)
    setError(null)
    try {
      await createCalendarNote({
        note: trimmed,
        note_at: localDateTime(pick.date, pick.startMinutes),
        resource_id: pick.resourceId,
      })
      toast.success('Note added to the calendar', {
        description: `${fmtDayDate(pick.date)}, ${minutesToLabel(pick.startMinutes)}${scopeLabel ? ` · ${scopeLabel}` : ''}. The slot is still bookable.`,
      })
      onCreated?.()
      onOpenChange(false)
    } catch (e) {
      setError(readApiError(e))
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add a note</DialogTitle>
          <DialogDescription>
            {pick
              ? `${fmtDayDate(pick.date)} · ${minutesToLabel(pick.startMinutes)}${scopeLabel ? ` · ${scopeLabel}` : ''}`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {pick ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <SectionLabel>Note</SectionLabel>
              {/* Free text by definition — a memo is whatever the operator needs to
                  remember. Capped so it stays a calendar chip. */}
              <textarea
                value={note}
                autoFocus
                rows={4}
                maxLength={MAX_NOTE_CHARS}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Stock delivery arriving · Ella finishes at 3 · call the plumber back"
                aria-label="Note"
                className="w-full resize-y rounded-lg border border-border bg-background px-2.5 py-2 text-[14px] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-muted-foreground">
                  {scopeLabel
                    ? `Shown on the ${scopeLabel} column.`
                    : 'Shown against the day rather than a machine.'}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {trimmed.length}/{MAX_NOTE_CHARS}
                </span>
              </div>
            </div>

            {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <ConfirmButton
                label="Add note"
                icon={<StickyNote className="size-4" aria-hidden />}
                busy={saving}
                disabled={!canSave}
                onClick={() => void save()}
              />
              {/* Said twice — here and in the toast — because the whole point of
                  this feature is the thing it does NOT do. */}
              <p className="text-center text-[11px] text-muted-foreground">
                The time stays bookable. To hold it so nobody can book, block the time instead.
              </p>
              <p className="text-center text-[11px] text-muted-foreground">
                A note about a person belongs on their record — ask in chat.
              </p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
